import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

function Section({ label, title, children }: { label: string; title: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <p className="spec-label mb-1">{label}</p>
      <h2 className="font-display text-2xl font-semibold">{title}</h2>
      <div className="mt-2 space-y-3 text-muted-foreground">{children}</div>
    </section>
  )
}

export default function ApproachPage() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <p className="spec-label mb-2">The approach</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold">Why this warm-up</h1>
      <p className="mt-3 text-muted-foreground">
        A complete warm-up wakes the whole voice before you ask it to do coordinated work in
        real music. Here's the thinking, kept honest.
      </p>

      <Section label="The backbone" title="Ease-in → chest → head → mix">
        <p>
          Every warm-up we build touches four things, in order: a gentle ease-in, the chest
          register, the head register, and the mix that coordinates them. We cover all four on
          purpose — not because every song needs all four, but because the coordination you
          didn't warm up is the one that lets you down when the music finally asks for it.
        </p>
        <p>
          Working the whole instrument proactively means nothing is cold when you reach for it.
        </p>
      </Section>

      <Section label="The honest science" title="Two mechanisms, one coordination">
        <p>
          Chest and head are two real, distinct laryngeal mechanisms — the lower (M1,
          thyroarytenoid-dominant) and the upper (M2, cricothyroid-dominant). Those are physical
          things your larynx does.
        </p>
        <p>
          "Mix" is not a secret third register with its own machinery. It's the balanced
          coordination between chest and head in the overlap zone — the passaggio. We still give
          it its own slot, because that coordination is worth drilling proactively so it's
          already there when a phrase sits right in the transition.
        </p>
      </Section>

      <Section label="Start gentle" title="Why ease-in comes first">
        <p>
          We open with gentle semi-occluded exercises — hums, lip trills, straw phonation, "ng".
          Narrowing the vocal tract balances the pressures across the vocal folds, so they start
          moving with the least strain. It's the evidence-based way to begin: cushion the folds
          and wake the breath before any real range work.
        </p>
      </Section>

      <Section label="Come back down" title="Why a cool-down after belting">
        <p>
          Belting sits in upper chest carried up into the passaggio — effortful, and the one
          place the research is emphatic about recovery. When you opt into belt, we automatically
          add a gentle cool-down at the end: light semi-occluded work again, to release the effort
          and settle the voice before you stop.
        </p>
      </Section>

      <div className="mt-12 rounded-lg border bg-card p-4">
        <p className="text-sm text-muted-foreground">
          The short version: warm the whole instrument, start soft, coordinate the transition, and
          come back down after heavy use.
        </p>
        <Link
          to="/"
          className="mt-4 inline-flex min-h-[44px] items-center rounded-full bg-primary px-6 font-medium text-primary-foreground"
        >
          Build a warm-up
        </Link>
      </div>
    </div>
  )
}
